import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, HostListener, Injector, OnDestroy, OnInit, afterNextRender, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelect, MatSelectModule } from '@angular/material/select';

import { environment } from '../../../environments/environment';
import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { CommonService } from '@app/common/common.service';
import { LoginService } from '@app/common/login.service';
import { HrService } from '@app/hr/hr.service';
import { AlertService } from '@app/shared/alert.service';
import { C_value, Store } from '@app/shared/_models/common';
import { C_VALUES_HABILITATION, C_VALUES_USER_ROLE, HABILITATION_ADMIN, SUPER_USER_ID } from '@app/shared/constants';
import { UserManagementService, PasswordState, SuperadminUserInput, SuperadminUserRow, superadminFailureOf } from './user-management.service';

const UNLOCK_TTL_MS = 5 * 60 * 1000;
const TTL_MESSAGE = 'Écran reverrouillé : 5 minutes sans appel réussi.';
const PASSWORD_MIN = 8;
const SUPER_PASSWORD_MIN = 16;
const PASSWORD_MAX = 128;
// Au moins le minimum du compte 1 (miroir serveur) : « Générer » convient à tous les comptes.
const GENERATED_LENGTH = SUPER_PASSWORD_MIN;
// Sans 0/O/o, 1/l/I : un mot de passe généré se dicte et se recopie sans hésitation.
const PASSWORD_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
const PASSWORD_LABELS: Record<PasswordState, string> = { argon2: 'Argon2', legacy: 'À reposer', none: 'Aucun' };

interface Choice {
  id: number;
  name: string;
  active: boolean;
}

interface UserDraft {
  id: number | null;
  store: number | null;
  lastname: string;
  firstname: string;
  login: string;
  email: string;
  habilitations: number[];
  roles: number[];
  password: string;
}

interface UserView {
  row: SuperadminUserRow;
  storeName: string;
  habilitationLabels: string;
  roleLabels: string;
  employeeLabel: string;
  passwordLabel: string;
  haystack: string;
}

@Component({
  selector: 'app-user-management',
  standalone: true,
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, MenuCommonComponent, MatButtonModule, MatCheckboxModule, MatFormFieldModule, MatIconModule, MatInputModule, MatSelectModule],
})
export class UserManagementComponent implements OnInit, OnDestroy {
  private service = inject(UserManagementService);
  private commonService = inject(CommonService);
  private hrService = inject(HrService);
  private loginService = inject(LoginService);
  private alertService = inject(AlertService);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);

  readonly SUPER_USER_ID = SUPER_USER_ID;
  readonly PASSWORD_MAX = PASSWORD_MAX;
  readonly cleartextWarning = isCleartextRemote(environment.server_url);
  readonly login = this.loginService.loggeduser?.login ?? '';
  readonly myId = this.loginService.loggeduser?.id ?? null;

  // Mot de passe de l'Admin connecté : champ privé seulement, jamais signal, stockage, URL ni console ; le serveur le revérifie à chaque appel.
  private ownPassword: string | null = null;
  private ttlTimer: ReturnType<typeof setTimeout> | null = null;
  // Échéance contrôlée avant chaque appel : la minuterie est suspendue ou retardée en onglet caché et en veille.
  private ttlDeadline = 0;
  // Incrémenté à chaque verrouillage : une réponse arrivée après ne rouvre rien.
  private lockGeneration = 0;

  readonly unlocked = signal(false);
  readonly busy = signal(false);
  readonly users = signal<SuperadminUserRow[]>([]);
  readonly stores = signal<Store[]>([]);
  readonly roles = signal<Choice[]>([]);
  readonly habilitations = signal<Choice[]>([]);
  readonly filter = signal('');
  readonly selectedId = signal<number | null>(null);
  readonly draft = signal<UserDraft | null>(null);

  private editor = viewChild('editor', { read: ElementRef });
  private firstField = viewChild('firstField', { read: MatSelect });

  readonly views = computed<UserView[]>(() => {
    const stores = new Map(this.stores().map(s => [s.id, s.name ?? ''] as const));
    const roles = new Map(this.roles().map(c => [c.id, c.name] as const));
    const habs = new Map(this.habilitations().map(c => [c.id, c.name] as const));
    return this.users().map(row => ({
      row,
      storeName: row.store == null ? '—' : stores.get(row.store) || '#' + row.store,
      habilitationLabels: (row.habilitations ?? []).map(id => habs.get(id) ?? '#' + id).join(', '),
      roleLabels: (row.employee?.roles ?? []).map(id => roles.get(id) ?? '#' + id).join(', '),
      employeeLabel: employeeLabelOf(row),
      passwordLabel: PASSWORD_LABELS[row.password_state] ?? row.password_state,
      haystack: fold([row.lastname, row.firstname, row.login, row.email].filter(Boolean).join(' ')),
    }));
  });

  readonly shown = computed(() => {
    const f = fold(this.filter().trim());
    return f ? this.views().filter(v => v.haystack.includes(f)) : this.views();
  });

  // Ligne telle que chargée : ce qu'elle détient reste proposé même une fois décoché dans le brouillon.
  private readonly loadedRow = computed(() => {
    const id = this.draft()?.id;
    return id == null ? undefined : this.users().find(u => u.id === id);
  });

  readonly roleChoices = computed(() => withOrphans(this.roles(), [...(this.draft()?.roles ?? []), ...(this.loadedRow()?.employee?.roles ?? [])]));

  readonly habilitationChoices = computed(() => withOrphans(this.habilitations(), [...(this.draft()?.habilitations ?? []), ...(this.loadedRow()?.habilitations ?? [])]));

  // Un rôle coché sans fiche salarié : le serveur crée une fiche minimale (id_user, magasin, rôles).
  readonly minimalFileNote = computed(() => {
    const d = this.draft();
    if (!d || d.roles.length === 0) return false;
    return d.id == null || !this.loadedRow()?.employee;
  });

  // Le serveur efface le mot de passe d'un compte sans identifiant : le dire avant qu'on enregistre.
  readonly passwordWipeWarning = computed(() => {
    const d = this.draft();
    return !!d && d.id !== SUPER_USER_ID && !d.login.trim() && (this.loadedRow()?.password_state ?? 'none') !== 'none';
  });

  ngOnInit(): void {
    forkJoin({
      stores: this.commonService.getStores().pipe(catchError(() => of([] as Store[]))),
      roles: this.hrService.valuesOf$(C_VALUES_USER_ROLE),
      habilitations: this.hrService.valuesOf$(C_VALUES_HABILITATION),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ stores, roles, habilitations }) => {
        this.stores.set(stores ?? []);
        this.roles.set(choicesOf(roles));
        this.habilitations.set(choicesOf(habilitations));
      });
  }

  ngOnDestroy(): void {
    this.lock();
  }

  // Au retour sur l'onglet : la minuterie a pu être retardée, l'échéance fait foi.
  @HostListener('document:visibilitychange')
  onVisibilityChange(): void {
    if (document.visibilityState === 'visible' && this.unlocked() && !this.busy() && Date.now() >= this.ttlDeadline) this.lock(TTL_MESSAGE);
  }

  unlock(input: HTMLInputElement): void {
    const pw = input.value;
    input.value = '';
    if (!pw || this.busy()) return;
    this.fetchUsers(pw, null);
  }

  lock(message?: string): void {
    this.lockGeneration++;
    this.ownPassword = null;
    if (this.ttlTimer) clearTimeout(this.ttlTimer);
    this.ttlTimer = null;
    this.ttlDeadline = 0;
    this.unlocked.set(false);
    this.busy.set(false);
    this.users.set([]);
    this.draft.set(null);
    this.selectedId.set(null);
    if (message) this.alertService.warn(message);
  }

  onFilter(event: Event): void {
    this.filter.set((event.target as HTMLInputElement).value);
  }

  // Pendant un envoi, changer de ligne remplacerait le brouillon dont on attend la réponse.
  pick(row: SuperadminUserRow): void {
    if (!this.busy()) this.edit(row, true);
  }

  private edit(row: SuperadminUserRow, focus: boolean): void {
    this.selectedId.set(row.id);
    this.draft.set({
      id: row.id,
      store: row.store,
      lastname: row.lastname ?? '',
      firstname: row.firstname ?? '',
      login: row.login ?? '',
      email: row.email ?? '',
      habilitations: [...(row.habilitations ?? [])],
      roles: [...(row.employee?.roles ?? [])],
      password: '',
    });
    this.showEditor(focus);
  }

  newUser(): void {
    const stores = this.stores();
    const store = [this.loginService.currentstore, this.loginService.loggeduser?.store].find(id => stores.some(s => s.id === id)) ?? stores[0]?.id ?? null;
    this.selectedId.set(null);
    this.draft.set({
      id: null,
      store,
      lastname: '',
      firstname: '',
      login: '',
      email: '',
      habilitations: [],
      roles: [],
      password: '',
    });
    this.showEditor(true);
  }

  cancel(): void {
    this.draft.set(null);
    this.selectedId.set(null);
  }

  patch<K extends keyof UserDraft>(key: K, value: UserDraft[K]): void {
    this.draft.update(d => (d ? { ...d, [key]: value } : d));
  }

  toggle(key: 'habilitations' | 'roles', id: number, checked: boolean): void {
    this.draft.update(d => {
      if (!d) return d;
      const next = d[key].filter(x => x !== id);
      return { ...d, [key]: checked ? [...next, id] : next };
    });
  }

  // Le serveur refuse toute modification du compte 1 venant d'un autre compte.
  isSuperUserLocked(d: UserDraft): boolean {
    return d.id === SUPER_USER_ID && this.myId !== SUPER_USER_ID;
  }

  // Le compte 1 garde toujours l'habilitation Admin : le serveur le refuserait sinon.
  isLockedAdmin(d: UserDraft, c: Choice): boolean {
    return d.id === SUPER_USER_ID && c.id === HABILITATION_ADMIN;
  }

  generate(): void {
    this.patch('password', generatePassword());
  }

  hasOuterSpaces(pw: string): boolean {
    return pw !== '' && pw !== pw.trim();
  }

  passwordMinOf(d: UserDraft): number {
    return passwordMin(d.id);
  }

  save(): void {
    const d = this.draft();
    const pw = this.ownPassword;
    if (!d || !pw || this.busy() || this.isSuperUserLocked(d)) return;
    if (Date.now() >= this.ttlDeadline) {
      this.lock(TTL_MESSAGE);
      return;
    }
    const problem = checkDraft(d);
    if (problem) {
      this.alertService.warn(problem);
      return;
    }
    const before = d.id == null ? undefined : this.users().find(u => u.id === d.id);
    const input = toInput(d, before);
    const gen = this.lockGeneration;
    this.busy.set(true);
    this.service.saveUser$(pw, input).subscribe({
      next: res => {
        if (gen !== this.lockGeneration) return;
        this.busy.set(false);
        this.armTtl();
        // L'Admin connecté vient de changer son propre mot de passe : les appels suivants doivent porter le nouveau.
        if (res.id === this.myId && res.password_set && input.password) this.ownPassword = input.password;
        // Id posé tout de suite : si le rechargement échoue, un nouvel envoi devient un UPDATE et non un second INSERT.
        this.draft.update(cur => (cur && cur.id === input.id ? { ...cur, id: res.id, password: '' } : cur));
        if (this.draft()?.id === res.id) this.selectedId.set(res.id);
        const parts = [(res.created ? 'Utilisateur n° ' + res.id + ' créé' : 'Utilisateur n° ' + res.id + ' enregistré') + (res.password_set ? ', mot de passe posé' : '') + '.'];
        if (res.employee_id != null && !before?.employee) parts.push('Fiche salarié minimale créée.');
        this.alertService.success(parts.join(' '));
        this.fetchUsers(this.ownPassword ?? pw, res.id);
      },
      error: e => {
        if (gen !== this.lockGeneration) return;
        this.busy.set(false);
        this.fail(e);
      },
    });
  }

  private fetchUsers(pw: string, reselectId: number | null): void {
    const gen = this.lockGeneration;
    this.busy.set(true);
    this.service.listUsers$(pw).subscribe({
      next: users => {
        if (gen !== this.lockGeneration) return;
        this.busy.set(false);
        this.ownPassword = pw;
        this.armTtl();
        this.users.set(users);
        this.unlocked.set(true);
        const row = reselectId == null ? undefined : users.find(u => u.id === reselectId);
        if (row) this.edit(row, false);
      },
      error: e => {
        if (gen !== this.lockGeneration) return;
        this.busy.set(false);
        this.fail(e);
      },
    });
  }

  private fail(e: unknown): void {
    const failure = superadminFailureOf(e);
    if (failure.relock) this.lock();
    this.alertService.error(failure.message);
  }

  private armTtl(): void {
    if (this.ttlTimer) clearTimeout(this.ttlTimer);
    this.ttlDeadline = Date.now() + UNLOCK_TTL_MS;
    this.ttlTimer = setTimeout(() => this.lock(TTL_MESSAGE), UNLOCK_TTL_MS);
  }

  // Pas de focus sur la resélection après envoi : la réponse arrive quand l'utilisateur est peut-être déjà ailleurs.
  private showEditor(focus: boolean): void {
    afterNextRender(
      () => {
        if (focus) this.firstField()?.focus({ preventScroll: true });
        this.editor()?.nativeElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      },
      { injector: this.injector },
    );
  }
}

// Bandeau seulement : JP sert la configuration `local` en http depuis d'autres postes du réseau.
function isCleartextRemote(url: string): boolean {
  if (!/^http:\/\//i.test(url)) return false;
  try {
    return !['localhost', '127.0.0.1', '[::1]'].includes(new URL(url).hostname);
  } catch {
    return true;
  }
}

function choicesOf(values: C_value[]): Choice[] {
  return values.filter(v => v.id != null).map(v => ({ id: v.id!, name: v.name ?? '#' + v.id, active: v.is_active !== false }));
}

function fold(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function employeeLabelOf(row: SuperadminUserRow): string {
  const e = row.employee;
  if (!e) return '—';
  if (e.leave_date) return 'Sortie le ' + frDate(e.leave_date);
  return e.is_active === false ? 'Désactivée' : 'Oui';
}

function frDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? m[3] + '/' + m[2] + '/' + m[1] : iso;
}

function textOrNull(s: string): string | null {
  const t = s.trim();
  return t === '' ? null : t;
}

function sortedUnique(ids: number[]): number[] {
  return [...new Set(ids)].sort((a, b) => a - b);
}

// Un id détenu hors référentiel reste une case décochable (active : sans « (inactif) »), sinon le serveur refuse chaque envoi.
// Rien tant que le référentiel manque : le gabarit dit alors « indisponible ».
function withOrphans(ref: Choice[], held: number[]): Choice[] {
  if (ref.length === 0) return ref;
  const known = new Set(ref.map(c => c.id));
  const orphans = sortedUnique(held.filter(id => !known.has(id))).map(id => ({ id, name: '#' + id + ' hors référentiel', active: true }));
  return [...ref.filter(c => c.active || held.includes(c.id)), ...orphans];
}

function passwordMin(id: number | null): number {
  return id === SUPER_USER_ID ? SUPER_PASSWORD_MIN : PASSWORD_MIN;
}

// Confort avant envoi ; le serveur refait toutes les validations et renvoie son propre message.
function checkDraft(d: UserDraft): string | null {
  if (d.store == null) return 'Choisissez un magasin.';
  if (!d.lastname.trim()) return 'Le nom est obligatoire.';
  if (d.id === SUPER_USER_ID && !d.login.trim()) return "L'identifiant du compte 1 est obligatoire.";
  if (d.password === '') return null;
  if (!d.login.trim()) return "Un mot de passe sans identifiant ne donne aucun accès : saisissez l'identifiant ou videz le mot de passe.";
  const length = [...d.password].length;
  const min = passwordMin(d.id);
  if (length < min || length > PASSWORD_MAX) return (d.id === SUPER_USER_ID ? 'Le mot de passe du compte 1' : 'Le mot de passe') + ' doit compter de ' + min + ' à ' + PASSWORD_MAX + ' caractères.';
  return null;
}

// Champs texte rognés (vide → null) ; le mot de passe jamais : le serveur le hache tel quel.
function toInput(d: UserDraft, before: SuperadminUserRow | undefined): SuperadminUserInput {
  const habilitations = new Set(d.habilitations);
  if (d.id === SUPER_USER_ID) habilitations.add(HABILITATION_ADMIN);
  const roles = sortedUnique(d.roles);
  const previousRoles = sortedUnique(before?.employee?.roles ?? []);
  return {
    id: d.id,
    store: d.store!,
    firstname: textOrNull(d.firstname),
    lastname: d.lastname.trim(),
    login: textOrNull(d.login),
    email: textOrNull(d.email),
    habilitations: sortedUnique([...habilitations]),
    // null = fiche salarié intouchée : les rôles ne partent que s'ils ont changé.
    roles: roles.length === previousRoles.length && roles.every((r, i) => r === previousRoles[i]) ? null : roles,
    password: d.password === '' ? null : d.password,
  };
}

// Tirage par rejet sur l'octet : sans biais de modulo, chaque caractère de l'alphabet est équiprobable.
function generatePassword(): string {
  const limit = 256 - (256 % PASSWORD_ALPHABET.length);
  const out: string[] = [];
  const buf = new Uint8Array(32);
  while (out.length < GENERATED_LENGTH) {
    crypto.getRandomValues(buf);
    for (const b of buf) {
      if (b < limit && out.length < GENERATED_LENGTH) out.push(PASSWORD_ALPHABET[b % PASSWORD_ALPHABET.length]);
    }
  }
  return out.join('');
}
